import { type FormEvent, useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  PERMISSIONS,
  isPracticalExamWizardStep,
  type PracticalExamSheetInput,
  type PracticalExamVehicle,
  type PracticalExamWizardStep,
} from '@lms/shared-types';
import { ConfirmModal, Loader, useToast } from '@lms/ui';
import { HubBackLink } from '@/components/hub/HubBackLink';
import { userOriginBack } from '@/lib/user-detail-navigation';
import { usePermissions } from '@/hooks/usePermissions';
import { api } from '@/lib/api';
import { roleActionErrorToast } from '@/lib/user-feedback';
import { PracticalExamCloseStep } from '@/features/practical-exams/PracticalExamCloseStep';
import { PracticalExamPractice, PracticalExamSummary } from '@/features/practical-exams/PracticalExamForm';
import {
  PracticalExamStudentStep,
  ExamStepHeader,
  type PracticalExamStudent,
} from '@/features/practical-exams/PracticalExamStudentStep';

interface StoredSheet extends PracticalExamSheetInput {
  id: string;
  passed: boolean;
  studentUserId: string | null;
  wizardStep?: PracticalExamWizardStep;
}

interface FleetVehicleOption {
  id: string;
  plate: string;
  brand: string;
  model: string;
  year: number | null;
  transmission: PracticalExamVehicle;
  active: boolean;
}

interface FleetVehicleList {
  data: FleetVehicleOption[];
}

interface PracticalExamInstitution {
  municipality: string;
  school: string;
  schoolCommune: string;
}

function compactPlate(value: string): string {
  return value.toUpperCase().replace(/[^A-Z0-9]/g, '');
}

type SheetStep = 'student' | 'vehicle' | 'km' | 'details' | 'sheet' | 'km-end' | 'student-sign' | 'examiner-sign';

type SheetPayload = PracticalExamSheetInput & {
  studentUserId: string | null;
  wizardStep: PracticalExamWizardStep;
};

function stepStorageKey(sheetId: string) {
  return `practical-exam-step:${sheetId}`;
}

function storedWizardStep(sheetId: string | undefined): PracticalExamWizardStep | null {
  if (!sheetId) return null;
  try {
    const value = sessionStorage.getItem(stepStorageKey(sheetId));
    return isPracticalExamWizardStep(value) ? value : null;
  } catch {
    return null;
  }
}

function storeWizardStep(sheetId: string, step: PracticalExamWizardStep) {
  try {
    sessionStorage.setItem(stepStorageKey(sheetId), step);
  } catch {
    // El navegador puede bloquear sessionStorage. El paso también se guarda en la ficha.
  }
}

function draftBody(
  sheet: PracticalExamSheetInput,
  studentUserId: string | null,
  wizardStep: PracticalExamWizardStep,
): SheetPayload {
  return { ...sheet, status: 'draft', studentUserId, wizardStep };
}

function hasSheetProgress(sheet: PracticalExamSheetInput, studentUserId: string | null): boolean {
  return Boolean(
    studentUserId ||
    sheet.applicantName.trim() ||
    sheet.run.trim() ||
    sheet.plate.trim() ||
    sheet.vehicleType ||
    sheet.kmStart != null ||
    sheet.kmEnd != null ||
    sheet.startedAt ||
    sheet.endedAt ||
    sheet.observations.trim() ||
    sheet.studentSignature ||
    sheet.examinerSignature ||
    Object.keys(sheet.faults).length > 0
  );
}

function nowTime(): string {
  const date = new Date();
  return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
}

function todayIso(): string {
  const date = new Date();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

function emptySheet(examinerName: string): PracticalExamSheetInput {
  return {
    applicantName: '',
    run: '',
    examDate: todayIso(),
    examinerName,
    municipality: '',
    startedAt: '',
    endedAt: '',
    kmStart: null,
    kmEnd: null,
    school: '',
    schoolCommune: '',
    plate: '',
    vehicleType: null,
    faults: {},
    observations: '',
    studentSignature: '',
    examinerSignature: '',
    status: 'draft',
  };
}

export function PracticalExamSheetPage() {
  const { id } = useParams();
  const isNew = !id;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { user, hasPermission } = usePermissions();
  const canSave = hasPermission(isNew ? PERMISSIONS.EVALUACIONES_CREAR : PERMISSIONS.EVALUACIONES_EDITAR);
  const canDelete = !isNew && hasPermission(PERMISSIONS.EVALUACIONES_ELIMINAR);
  const [searchParams, setSearchParams] = useSearchParams();
  const modo = searchParams.get('modo');
  const originBack = userOriginBack(searchParams, '/examen-practico', 'Volver a fichas');
  const resuming = !isNew && modo === 'reanudar';
  const editingData = !isNew && modo === 'editar';
  const [step, setStep] = useState<SheetStep>(isNew ? 'student' : editingData ? 'details' : 'sheet');
  const wizardStepRef = useRef<PracticalExamWizardStep>(isNew ? 'student' : 'sheet');
  const [studentUserId, setStudentUserId] = useState<string | null>(null);
  const [sheet, setSheet] = useState<PracticalExamSheetInput>(() => emptySheet(user?.name ?? ''));
  const [confirmDelete, setConfirmDelete] = useState(false);
  const hydrated = useRef(isNew);
  const userMoved = useRef(false);
  const finishing = useRef(false);
  const [draftReady, setDraftReady] = useState(isNew);
  const modoRef = useRef(modo);
  modoRef.current = modo;
  const session = useRef({
    id: id as string | undefined,
    skip: false,
    saved: '',
    savedStep: (isNew ? 'student' : 'sheet') as PracticalExamWizardStep,
    chain: Promise.resolve() as Promise<unknown>,
    leave: null as object | null,
  });

  const persistSheet = useCallback((body: SheetPayload) => {
    const current = session.current;
    const run = current.chain.then(async () => {
      const sheetId = current.id;
      const saved = await api<StoredSheet>(sheetId ? `/practical-exams/${sheetId}` : '/practical-exams', {
        method: sheetId ? 'PATCH' : 'POST',
        body,
      });
      current.id = saved.id;
      current.savedStep = body.wizardStep;
      current.saved = JSON.stringify(draftBody(body, body.studentUserId, body.wizardStep));
      if (saved.id) storeWizardStep(saved.id, wizardStepRef.current);
      try {
        sessionStorage.removeItem(stepStorageKey('new'));
      } catch {
        // Sin almacenamiento, el paso queda en la ficha guardada.
      }
      return saved;
    });
    current.chain = run.then(
      () => undefined,
      () => undefined,
    );
    return run;
  }, []);

  const query = useQuery({
    queryKey: ['practical-exams', id],
    enabled: !isNew,
    queryFn: () => api<StoredSheet>(`/practical-exams/${id}`),
  });

  const vehiclesQuery = useQuery({
    queryKey: ['practical-exam-vehicles', 'sheet-options'],
    queryFn: () => api<FleetVehicleList>('/practical-exams/vehicles?perPage=100'),
  });

  const institutionQuery = useQuery({
    queryKey: ['practical-exam-institution'],
    queryFn: () => api<PracticalExamInstitution>('/practical-exams/institution'),
  });

  useEffect(() => {
    if (!query.data) return;
    const loaded: PracticalExamSheetInput = {
      applicantName: query.data.applicantName,
      run: query.data.run,
      examDate: query.data.examDate,
      examinerName: query.data.examinerName,
      municipality: query.data.municipality,
      startedAt: query.data.startedAt,
      endedAt: query.data.endedAt,
      kmStart: query.data.kmStart,
      kmEnd: query.data.kmEnd,
      school: query.data.school,
      schoolCommune: query.data.schoolCommune,
      plate: query.data.plate,
      vehicleType: query.data.vehicleType,
      faults: query.data.faults,
      observations: query.data.observations,
      studentSignature: query.data.studentSignature ?? '',
      examinerSignature: query.data.examinerSignature ?? '',
      status: query.data.status === 'final' ? 'final' : 'draft',
    };
    setSheet(loaded);
    setStudentUserId(query.data.studentUserId);
    hydrated.current = true;
    setDraftReady(true);
    session.current.id = query.data.id;
    const loadedStep = isPracticalExamWizardStep(query.data.wizardStep) ? query.data.wizardStep : 'student';
    const openStep = storedWizardStep(query.data.id) ?? loadedStep;
    if (!userMoved.current) {
      if (modoRef.current === 'reanudar' && loaded.status !== 'final') {
        wizardStepRef.current = openStep;
        setStep(openStep);
      } else {
        wizardStepRef.current = loadedStep;
      }
    }
    session.current.savedStep = loadedStep;
    session.current.saved = JSON.stringify(draftBody(loaded, query.data.studentUserId, loadedStep));
  }, [query.data]);

  useEffect(() => {
    const school = institutionQuery.data;
    if (!school) return;
    setSheet((current) => ({
      ...current,
      municipality: school.municipality,
      school: school.school,
      schoolCommune: school.schoolCommune,
    }));
  }, [institutionQuery.data, query.data]);

  const takingExam =
    !isNew &&
    (modo === 'examen' || (resuming && step === 'sheet' && sheet.status !== 'final' && !sheet.examinerSignature));
  const finished = sheet.status === 'final';
  const practicing = !finished;
  const latest = useRef({
    sheet,
    studentUserId,
    canSave,
    practicing,
    toast,
    invalidate: () =>
      queryClient.invalidateQueries({
        queryKey: ['practical-exams'],
        predicate: (item) => item.queryKey.length !== 2,
      }),
  });
  latest.current = {
    sheet,
    studentUserId,
    canSave,
    practicing,
    toast,
    invalidate: () =>
      queryClient.invalidateQueries({
        queryKey: ['practical-exams'],
        predicate: (item) => item.queryKey.length !== 2,
      }),
  };

  useEffect(() => {
    if (finishing.current || !draftReady || !hydrated.current || !canSave || !practicing || !hasSheetProgress(sheet, studentUserId)) return;
    const body = draftBody(sheet, studentUserId, wizardStepRef.current);
    if (JSON.stringify(body) === session.current.saved) return;
    const delay = wizardStepRef.current === session.current.savedStep ? 600 : 0;
    const timer = window.setTimeout(() => {
      void persistSheet(body)
        .then(() => latest.current.invalidate())
        .catch(() => undefined);
    }, delay);
    return () => window.clearTimeout(timer);
  }, [sheet, studentUserId, step, canSave, practicing, persistSheet, draftReady]);

  useEffect(() => {
    const sessionRef = session.current;
    return () => {
      const token = {};
      sessionRef.leave = token;
      window.setTimeout(() => {
        if (sessionRef.leave !== token || sessionRef.skip || finishing.current) return;
        const snap = latest.current;
        if (!snap.canSave || !hydrated.current || !snap.practicing || !hasSheetProgress(snap.sheet, snap.studentUserId)) return;
        const body = draftBody(snap.sheet, snap.studentUserId, wizardStepRef.current);
        if (JSON.stringify(body) === sessionRef.saved) return;
        void persistSheet(body)
          .then(() => {
            snap.toast({
              title: 'La ficha quedó en borrador',
              description: 'Puedes seguirla desde el listado de fichas.',
              variant: 'success',
            });
            return snap.invalidate();
          })
          .catch(() => {
            snap.toast({ title: 'No se pudo guardar el borrador', variant: 'danger' });
          });
      }, 0);
    };
  }, [persistSheet]);

  function patch(partial: Partial<PracticalExamSheetInput>) {
    setSheet((current) => ({ ...current, ...partial }));
  }

  function setCount(faultId: string, raw: string) {
    const count = raw === '' ? 0 : Number(raw);
    setSheet((current) => {
      const faults = { ...current.faults };
      if (!Number.isInteger(count) || count <= 0) delete faults[faultId];
      else faults[faultId] = Math.min(count, 99);
      return { ...current, faults };
    });
  }

  const vehicles = vehiclesQuery.data?.data ?? [];
  const selectedVehicle = vehicles.find((vehicle) => compactPlate(vehicle.plate) === compactPlate(sheet.plate)) ?? null;

  const save = useMutation({
    mutationFn: (body: SheetPayload) => persistSheet(body),
    onSuccess: async (saved) => {
      finishing.current = false;
      setSheet((current) => ({ ...current, status: saved.status === 'final' ? 'final' : 'draft' }));
      await queryClient.invalidateQueries({ queryKey: ['practical-exams'] });
      toast({
        title: saved.status === 'final' ? 'Ficha finalizada' : 'Ficha guardada',
        variant: 'success',
      });
      if (isNew) {
        session.current.skip = true;
        navigate(`/examen-practico/${saved.id}`, { replace: true });
      } else {
        setStep('sheet');
        setSearchParams((current) => {
          const next = new URLSearchParams(current);
          next.delete('modo');
          return next;
        }, { replace: true });
      }
    },
    onError: (error) => {
      finishing.current = false;
      setSheet((current) => ({ ...current, status: 'draft' }));
      toast(roleActionErrorToast(error, 'No se pudo terminar la ficha'));
    },
  });

  const remove = useMutation({
    mutationFn: () => api(`/practical-exams/${id}`, { method: 'DELETE' }),
    onSuccess: async () => {
      session.current.skip = true;
      await queryClient.invalidateQueries({ queryKey: ['practical-exams'] });
      toast({ title: 'Ficha eliminada', variant: 'success' });
      navigate('/examen-practico');
    },
    onError: (error) => toast(roleActionErrorToast(error, 'No se pudo eliminar la ficha')),
  });

  function rememberWizard(next: PracticalExamWizardStep) {
    userMoved.current = true;
    wizardStepRef.current = next;
    storeWizardStep(session.current.id ?? 'new', next);
  }

  function goTo(next: SheetStep) {
    if (next !== 'details' && isPracticalExamWizardStep(next)) rememberWizard(next);
    else userMoved.current = true;
    setStep(next);
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    if (!canSave || finished || !sheet.vehicleType || save.isPending) return;
    const next = { ...sheet, status: 'final' as const };
    finishing.current = true;
    setSheet(next);
    save.mutate({ ...next, studentUserId, wizardStep: wizardStepRef.current });
  }

  function chooseStudent(student: PracticalExamStudent) {
    setStudentUserId(student.id);
    patch({
      applicantName: student.name,
      run: student.run,
    });
  }

  function endExam() {
    if (!canSave || !sheet.vehicleType) return;
    if (!sheet.endedAt) patch({ endedAt: nowTime() });
    goTo('km-end');
  }

  function leaveExam() {
    const next = new URLSearchParams(searchParams);
    next.delete('modo');
    setSearchParams(next, { replace: true });
  }

  function showSheet(remember = true) {
    if (remember) rememberWizard('sheet');
    else userMoved.current = true;
    setStep('sheet');
    if (searchParams.get('modo') === 'editar') {
      setSearchParams((current) => {
        const next = new URLSearchParams(current);
        next.delete('modo');
        return next;
      }, { replace: true });
    }
  }

  if (!isNew && (query.isLoading || (resuming && !hydrated.current && !query.error))) {
    return (
      <div className="flex justify-center py-12">
        <Loader />
      </div>
    );
  }

  if (step === 'km-end' || step === 'student-sign' || step === 'examiner-sign') {
    return (
      <PracticalExamCloseStep
        phase={step}
        sheet={sheet}
        disabled={!canSave}
        pending={save.isPending}
        onBack={
          step === 'km-end'
            ? () => goTo('sheet')
            : step === 'student-sign'
              ? () => goTo('km-end')
              : () => goTo('student-sign')
        }
        onPatch={patch}
        onContinue={() => {
          if (step === 'km-end') goTo('student-sign');
          else if (step === 'student-sign') goTo('examiner-sign');
          else {
            goTo('sheet');
            if (searchParams.get('modo')) leaveExam();
          }
        }}
      />
    );
  }

  if (step !== 'sheet') {
    return (
      <PracticalExamStudentStep
        phase={step}
        sheet={sheet}
        studentUserId={studentUserId}
        initialVehicleId={selectedVehicle?.id ?? ''}
        disabled={!canSave}
        onBack={
          step === 'vehicle'
            ? () => goTo('student')
            : step === 'km'
              ? () => goTo('vehicle')
              : step === 'details'
                ? () => showSheet(false)
                : undefined
        }
        onPatch={patch}
        onStudent={chooseStudent}
        onContinue={() => {
          if (step === 'student') goTo('vehicle');
          else if (step === 'vehicle') goTo('km');
          else {
            if (step === 'km' && !sheet.startedAt) patch({ startedAt: nowTime() });
            showSheet();
          }
        }}
      />
    );
  }

  const intake = isNew && !sheet.examinerSignature;

  return (
    <div className="lms-site-admin-hub-page font-sans">
      <style>{`
        .practical-exam-print-only { display: none; }
        @media print {
          body * { visibility: hidden; }
          .practical-exam-print, .practical-exam-print * { visibility: visible; }
          .practical-exam-print { position: absolute; top: 0; left: 0; width: 100%; background: white; }
          .practical-exam-print-only { display: block !important; }
          .practical-exam-no-print { display: none !important; }
          @page { size: A4; margin: 8mm; }
        }
      `}</style>
      {takingExam || intake ? (
        <ExamStepHeader
          className="practical-exam-no-print"
          title="Ficha práctica"
          text="Ficha del examen práctico, con los datos, las faltas y las firmas."
          step={4}
          status={sheet.status === 'final' ? 'final' : 'draft'}
          backLabel={originBack.label}
          backTo={originBack.to}
        />
      ) : (
        <div className="practical-exam-no-print mb-6 flex flex-col justify-between gap-4 border-b border-dash-outline-variant pb-5 sm:flex-row sm:items-center">
          <div className="flex items-start gap-4">
            <HubBackLink to={originBack.to} label={originBack.label} />
            <div>
              <h1 className="text-3xl font-semibold text-dash-primary md:text-4xl">
                Resumen de la evaluación
              </h1>
              <p className="mt-1 text-dash-on-surface-variant">
                Ficha del examen práctico, con los datos, las faltas y las firmas.
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {canSave && !finished ? (
              <button type="button" onClick={() => goTo('details')} className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-dash-outline-variant bg-white px-3.5 text-xs font-semibold text-dash-on-surface transition-colors hover:bg-[#f4f6fb]">
                <span className="material-symbols-outlined text-[16px]" aria-hidden="true">edit</span>
                Editar datos
              </button>
            ) : null}
            {canDelete ? (
              <button type="button" onClick={() => setConfirmDelete(true)} className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-red-200 bg-red-50 px-3.5 text-xs font-semibold text-error-600 transition-colors hover:bg-red-100">
                <span className="material-symbols-outlined text-[16px]" aria-hidden="true">delete</span>
                Eliminar
              </button>
            ) : null}
            <button type="button" onClick={() => window.print()} className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-dash-outline-variant bg-white px-3.5 text-xs font-semibold text-dash-on-surface transition-colors hover:bg-[#f4f6fb]">
              <span className="material-symbols-outlined text-[16px]" aria-hidden="true">print</span>
              Imprimir
            </button>
            {finished ? (
              <span className="inline-flex h-9 items-center gap-1.5 rounded-full bg-slate-100 px-3 text-xs font-semibold text-slate-700">
                <span className="h-1.5 w-1.5 rounded-full bg-slate-500" aria-hidden="true" />
                Finalizada
              </span>
            ) : canSave ? (
              <button
                type="submit"
                form="practical-exam-sheet"
                disabled={save.isPending || !sheet.vehicleType}
                className="inline-flex h-9 items-center gap-1.5 rounded-lg bg-[#000256] px-4 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-black disabled:opacity-60"
              >
                <span className="material-symbols-outlined text-[16px]" aria-hidden="true">save</span>
                {save.isPending ? 'Terminando…' : 'Terminar ficha'}
              </button>
            ) : null}
          </div>
        </div>
      )}

      {!isNew && query.error ? (
        <p className="mb-4 text-sm text-error-600" role="alert">
          {(query.error as Error).message}
        </p>
      ) : null}

      <form id="practical-exam-sheet" onSubmit={submit} className="practical-exam-print space-y-4">
        {intake || takingExam ? (
          <PracticalExamPractice
            sheet={sheet}
            readOnly={!canSave}
            onCount={setCount}
            onObservations={(observations) => patch({ observations })}
          />
        ) : (
          <PracticalExamSummary
            sheet={sheet}
            readOnly={!canSave || finished}
            onObservations={(observations) => patch({ observations })}
          />
        )}

        {intake || takingExam ? (
          <footer className="practical-exam-no-print flex justify-end">
            <button
              type="button"
              onClick={endExam}
              disabled={!canSave || save.isPending || !sheet.vehicleType}
              className="inline-flex h-10 w-full items-center justify-center gap-2 rounded-lg bg-[#000256] px-6 text-sm font-semibold text-white transition-colors hover:bg-black disabled:opacity-60 sm:w-auto"
            >
              <span>Continuar al Paso 5: Kilometraje final</span>
              <span className="material-symbols-outlined text-[18px]" aria-hidden="true">arrow_forward</span>
            </button>
          </footer>
        ) : null}
      </form>

      <ConfirmModal
        open={confirmDelete}
        title="Eliminar ficha"
        description="Se borra esta ficha de evaluación. Esta acción no se puede deshacer."
        confirmLabel="Eliminar"
        loading={remove.isPending}
        onClose={() => setConfirmDelete(false)}
        onConfirm={() => remove.mutate()}
      />
    </div>
  );
}
